import { fetchAuthSession, signOut } from "aws-amplify/auth";
import type {
  OrgTemplate,
  OrgTemplateListResponse,
  OrgTemplateSchema,
  CreateOrgTemplateInput,
  UpdateOrgTemplateInput,
  TemplateVersion,
  TemplateVersionListResponse,
  UpdateFieldMappingsInput,
  UploadUrlResponse,
  StagedUploadUrlResponse,
  StageVersionInput,
  StageVersionResponse,
  ReferencePdfUploadUrlResponse,
  ReferencePdfUrlResponse,
  ReferencePdfMode,
  TagIssue,
  MigrateClauseInput,
  MigrateClauseResult,
} from "../types/orgTemplate";
import type {
  CreateReportExportInput,
  DeliveryRecordType,
  DeliveryReportModel,
  FinancialReportModel,
  KpiReportModel,
  ReportCommentary,
  ReportCommentaryPutInput,
  ReportRange,
  ServiceCredit,
  ServiceCreditDeleteInput,
  ServiceCreditPutInput,
  SummaryReportModel,
} from "../types/report";
import type { DashboardSummaryResponse } from "../types/dashboardSummary";
import type { RecentEntry, NavRecentsResponse } from "../types/navRecents";
import type {
  Contract,
  ContractListResponse,
  CreateContractInput,
  UpdateContractInput,
  Template,
  TemplateWithSchema,
  TemplateListResponse,
  GenerationResult,
  Clause,
  ClauseListResponse,
  CreateClauseInput,
  UpdateClauseInput,
  Organisation,
  OrganisationListResponse,
  OrganisationUser,
  OrganisationUserListResponse,
  OrganisationStats,
  CreateOrganisationInput,
  UpdateOrganisationInput,
  CreateOrganisationUserInput,
  UpdateOrganisationUserInput,
  GlobalAdminUser,
  CreateGlobalAdminUserInput,
  ProfileResponse,
  UpdateProfileInput,
  NotificationPreferences,
  UpdateNotificationPreferences,
  LookupCategory,
  LookupItem,
  LookupItemListResponse,
  CreateLookupItemInput,
  UpdateLookupItemInput,
  Supplier,
  SupplierListResponse,
  SupplierDetailResponse,
  CreateSupplierInput,
  UpdateSupplierInput,
  ManagedContract,
  ManagedContractListResponse,
  ContractRegisterResponse,
  CreateManagedContractInput,
  UpdateManagedContractInput,
  PerformanceHistoryResponse,
  TrackingType,
  TrackingItem,
  TrackingItemListResponse,
  TrackingCountsResponse,
  TrackingListParams,
  KpiBatchRequest,
  KpiBatchResponse,
  CreateTrackingInput,
  UpdateTrackingInput,
  ArchiveTrackingInput,
  GenerateOccurrencesResponse,
  OccurrencePreviewResponse,
  RejectVariationInput,
  Variation,
  Sow,
  CorrectOriginalValueInput,
  CorrectStatusInput,
  CorrectVariationInput,
  Issue,
  TransitionIssueInput,
  CloseIssueInput,
  AppendThreadCommentInput,
  AppendThreadActionInput,
  MarkActionDoneInput,
  UserDisplayBatchResponse,
  ContractExport,
  CreateExportInput,
  CreateExportResponse,
} from "../types";
import type {
  FinanceSummary,
  PurchaseOrderListResponse,
  PurchaseOrderWithDerived,
  PurchaseOrder,
  CreatePurchaseOrderInput,
  UpdatePurchaseOrderInput,
  InvoiceListResponse,
  OrgWideInvoiceListResponse,
  InvoiceWithDerived,
  Invoice,
  InvoiceStatus,
  CreateInvoiceInput,
  UpdateInvoiceInput,
  ApproveInvoiceInput,
  ApproveInvoiceResponse,
  SetupResponse,
  FinanceSetupPutInput,
} from "../types/finance";

/**
 * Structured API error preserving HTTP status so callers can distinguish
 * 409 conflict from 404/500/network. Backwards-compatible: `.message` still
 * reads identically for existing callers that don't inspect `.status`.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly details?: unknown;
  readonly payload?: Record<string, unknown>;
  constructor(
    message: string,
    status: number,
    code?: string,
    details?: unknown,
    payload?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.payload = payload;
  }
}

/** True for 409 Conflict — used by forms to render "reload and retry" UX. */
export function isConflictError(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 409;
}

/**
 * The clause-markup issues carried by a 400 that rejected a template
 * activation, or null if this error is not a tag-validation rejection. Lets the
 * upload modal list each problem instead of showing a flattened message.
 */
export function getTagValidationIssues(err: unknown): TagIssue[] | null {
  if (!(err instanceof ApiError) || err.status !== 400) return null;
  const { details } = err;
  if (!Array.isArray(details) || details.length === 0) return null;
  const issues = details.filter(
    (d): d is TagIssue =>
      typeof d === "object" &&
      d !== null &&
      typeof (d as TagIssue).code === "string" &&
      typeof (d as TagIssue).message === "string",
  );
  return issues.length > 0 ? issues : null;
}

/**
 * The tags an active template still references, carried by a 409 that rejected a
 * clause rename/deletion/option change, or null if this error is not that
 * conflict. Lets the clause admin list what must be migrated first.
 */
export function getClauseConflictTags(err: unknown): string[] | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const details = err.details as { referencingTags?: unknown } | undefined;
  const tags = details?.referencingTags;
  if (!Array.isArray(tags) || tags.length === 0) return null;
  return tags.filter((t): t is string => typeof t === "string");
}

const API_BASE_URL = import.meta.env.VITE_API_URL || "";

/**
 * Build a query-string suffix (`""` or `"?a=1&b=2"`) from a params record.
 * A key is included when its value is not `undefined`, `null`, or `""`.
 * This preserves the per-call-site conventions the inlined builders used:
 *  - string params were truthy-skipped (empty string omitted) — matched
 *    by the `!== ""` rule;
 *  - boolean params used a defined-check (`activeOnly=false` IS sent) —
 *    `false` is not undefined/null/"" so it is kept;
 *  - numeric params (limit/page) only ever receive positive ints from
 *    callers, so the only theoretical divergence (`0`) is unreachable.
 */
function buildQuery(
  params: Record<string, string | number | boolean | undefined | null>,
): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      sp.set(key, String(value));
    }
  }
  const q = sp.toString();
  return q ? `?${q}` : "";
}

/**
 * Action taken when a request fails authentication unrecoverably — a 401 that
 * persists even after a forced token refresh, meaning the Cognito refresh
 * token is expired/revoked and Amplify can no longer mint a fresh ID token.
 * The default clears the stale session and hard-redirects to the login screen
 * (a full navigation, so all in-memory React Query / auth state is dropped),
 * rather than stranding the user on a page where every request 401s. Exposed
 * as a setter so tests can substitute a spy; the app relies on the default.
 */
type AuthExpiryHandler = () => void | Promise<void>;

async function defaultAuthExpiryHandler(): Promise<void> {
  try {
    await signOut();
  } catch {
    // Sign-out failure must not block the redirect — we're leaving anyway.
  }
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
}

let authExpiryHandler: AuthExpiryHandler = defaultAuthExpiryHandler;

/** Override the unrecoverable-auth handler, or reset to the default with `null`. */
export function setAuthExpiryHandler(handler: AuthExpiryHandler | null): void {
  authExpiryHandler = handler ?? defaultAuthExpiryHandler;
}

/**
 * Issue a single authenticated request. The Cognito ID token is fetched
 * per-call; passing `forceRefresh` makes Amplify mint a fresh token instead of
 * returning the cached (possibly expired) one — used for the one retry after
 * an initial 401.
 */
async function performRequest(
  endpoint: string,
  options: RequestInit,
  forceRefresh: boolean,
): Promise<Response> {
  const session = await fetchAuthSession(
    forceRefresh ? { forceRefresh: true } : undefined,
  );
  const token = session.tokens?.idToken?.toString();

  // Only set Content-Type for requests with a body
  const headers: HeadersInit = {
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(options.body && { "Content-Type": "application/json" }),
  };

  return fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  let response: Response;
  try {
    response = await performRequest(endpoint, options, false);

    // A 401 means the JWT authorizer rejected our token — almost always an ID
    // token that lapsed mid-session. Force one refresh and retry before giving
    // up, so a transient expiry recovers transparently.
    if (response.status === 401) {
      response = await performRequest(endpoint, options, true);
    }
  } catch (err) {
    // Network / auth failure — wrap as ApiError with status 0 so callers
    // get a single error shape regardless of origin.
    throw new ApiError(
      err instanceof Error ? err.message : "Network error",
      0,
    );
  }

  // Still unauthorised after a forced refresh → the refresh token is dead and
  // the session is unrecoverable. Clear it and bounce to login.
  if (response.status === 401) {
    await authExpiryHandler();
    throw new ApiError("Session expired", 401, "SESSION_EXPIRED");
  }

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ error: { message: "Request failed" } }));
    const errorInfo = errorData.error || {};
    let message = errorInfo.message || `HTTP ${response.status}`;

    // Include validation details if present
    if (errorInfo.details && Array.isArray(errorInfo.details)) {
      const detailMessages = errorInfo.details
        .map(
          (d: { path: string; message: string }) => `${d.path}: ${d.message}`,
        )
        .join("; ");
      message = `${message}: ${detailMessages}`;
    }

    throw new ApiError(
      message,
      response.status,
      errorInfo.code,
      errorInfo.details,
      errorInfo,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

export const dashboardApi = {
  // Server-side aggregation of every dashboard metric — one request replaces
  // the old drain-all-pages approach. Response keys mirror the caller's held
  // areas; see types/dashboardSummary.ts for the contract.
  summary: (): Promise<DashboardSummaryResponse> => {
    return apiRequest("/dashboard/summary");
  },
};

export const contractsApi = {
  list: (params?: {
    status?: string;
    limit?: number;
    nextToken?: string;
  }): Promise<ContractListResponse> => {
    return apiRequest(
      `/contracts${buildQuery({
        status: params?.status,
        limit: params?.limit,
        nextToken: params?.nextToken,
      })}`,
    );
  },

  get: (id: string): Promise<Contract> => {
    return apiRequest(`/contracts/${id}`);
  },

  create: (input: CreateContractInput): Promise<Contract> => {
    return apiRequest("/contracts", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  update: (id: string, input: UpdateContractInput): Promise<Contract> => {
    return apiRequest(`/contracts/${id}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  delete: (id: string): Promise<void> => {
    return apiRequest(`/contracts/${id}`, {
      method: "DELETE",
    });
  },

  generate: (id: string, async = false): Promise<GenerationResult> => {
    return apiRequest(`/contracts/${id}/generate?async=${async}`, {
      method: "POST",
    });
  },

  getGenerationStatus: (id: string): Promise<GenerationResult> => {
    return apiRequest(`/contracts/${id}/generation-status`);
  },

  downloadVersion: (
    contractId: string,
    version: number,
  ): Promise<{ documentUrl: string }> => {
    return apiRequest(`/contracts/${contractId}/versions/${version}/download`);
  },
};

export const templatesApi = {
  list: (params?: {
    activeOnly?: boolean;
    limit?: number;
    nextToken?: string;
  }): Promise<TemplateListResponse> => {
    return apiRequest(
      `/templates${buildQuery({
        activeOnly: params?.activeOnly,
        limit: params?.limit,
        nextToken: params?.nextToken,
      })}`,
    );
  },

  get: (id: string): Promise<Template> => {
    return apiRequest(`/templates/${id}`);
  },

  getWithSchema: (id: string): Promise<TemplateWithSchema> => {
    return apiRequest(`/templates/${id}?includeSchema=true`);
  },

  /**
   * Get presigned URL for downloading reference PDF.
   * Returns null if no reference PDF is configured.
   */
  getReferencePdfUrl: (id: string): Promise<ReferencePdfUrlResponse> => {
    return apiRequest(`/templates/${id}/reference-pdf-url`);
  },

  /**
   * Get presigned URL for uploading reference PDF (global-admin only).
   */
  getReferencePdfUploadUrl: (
    id: string,
  ): Promise<ReferencePdfUploadUrlResponse> => {
    return apiRequest(`/templates/${id}/reference-pdf-upload-url`, {
      method: "POST",
    });
  },

  /**
   * Confirm reference PDF upload (global-admin only).
   */
  confirmReferencePdf: (id: string, pdfKey: string): Promise<Template> => {
    return apiRequest(`/templates/${id}/reference-pdf`, {
      method: "PUT",
      body: JSON.stringify({ pdfKey }),
    });
  },

  /**
   * Remove reference PDF (global-admin only).
   */
  deleteReferencePdf: (id: string): Promise<void> => {
    return apiRequest(`/templates/${id}/reference-pdf`, {
      method: "DELETE",
    });
  },
};

export const clausesApi = {
  /**
   * List all clauses for a template.
   */
  list: (
    templateId: string,
    params?: { limit?: number; nextToken?: string },
  ): Promise<ClauseListResponse> => {
    return apiRequest(
      `/templates/${templateId}/clauses${buildQuery({
        limit: params?.limit,
        nextToken: params?.nextToken,
      })}`,
    );
  },

  /**
   * List every clause for a template, draining server pagination.
   * The server pages the list even when callers want the full set — the
   * wizard and admin surfaces must never act on a partial clause list.
   */
  listAll: async (templateId: string): Promise<ClauseListResponse> => {
    const items: ClauseListResponse['items'] = [];
    let nextToken: string | undefined;
    do {
      const page = await clausesApi.list(templateId, { limit: 100, nextToken });
      items.push(...page.items);
      nextToken = page.nextToken;
    } while (nextToken);
    return { items };
  },

  /**
   * Get a single clause by ID.
   */
  get: (templateId: string, clauseId: string): Promise<Clause> => {
    return apiRequest(`/templates/${templateId}/clauses/${clauseId}`);
  },

  /**
   * Create a new clause (admin only).
   */
  create: (templateId: string, input: CreateClauseInput): Promise<Clause> => {
    return apiRequest(`/templates/${templateId}/clauses`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  /**
   * Update an existing clause (admin only).
   */
  update: (
    templateId: string,
    clauseId: string,
    input: UpdateClauseInput,
  ): Promise<Clause> => {
    return apiRequest(`/templates/${templateId}/clauses/${clauseId}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  /**
   * Delete a clause (admin only).
   */
  delete: (templateId: string, clauseId: string): Promise<void> => {
    return apiRequest(`/templates/${templateId}/clauses/${clauseId}`, {
      method: "DELETE",
    });
  },

  /**
   * Guided rename migration (admin only, org templates only): rewrites the
   * active template's Word tags to match a clause and/or option identity
   * change, activating a new template version atomically with the clause
   * rename. See ClauseMigrationDialog.
   */
  migrate: (
    templateId: string,
    clauseId: string,
    input: MigrateClauseInput,
  ): Promise<MigrateClauseResult> => {
    return apiRequest(`/templates/${templateId}/clauses/${clauseId}/migrate`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
};

export const globalAdminApi = {
  listOrganisations: (params?: {
    limit?: number;
    nextToken?: string;
  }): Promise<OrganisationListResponse> => {
    return apiRequest(
      `/admin/organisations${buildQuery({
        limit: params?.limit,
        nextToken: params?.nextToken,
      })}`,
    );
  },

  getOrganisation: (id: string): Promise<Organisation> => {
    return apiRequest(`/admin/organisations/${id}`);
  },

  createOrganisation: (
    input: CreateOrganisationInput,
  ): Promise<Organisation> => {
    return apiRequest("/admin/organisations", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  updateOrganisation: (
    id: string,
    input: Partial<CreateOrganisationInput>,
  ): Promise<Organisation> => {
    return apiRequest(`/admin/organisations/${id}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  deleteOrganisation: (id: string): Promise<void> => {
    return apiRequest(`/admin/organisations/${id}`, {
      method: "DELETE",
    });
  },

  listOrganisationUsers: (
    orgId: string,
    params?: { limit?: number; nextToken?: string },
  ): Promise<OrganisationUserListResponse> => {
    return apiRequest(
      `/admin/organisations/${orgId}/users${buildQuery({
        limit: params?.limit,
        nextToken: params?.nextToken,
      })}`,
    );
  },

  listOrganisationContracts: (
    orgId: string,
    params?: { status?: string; limit?: number; nextToken?: string },
  ): Promise<ContractListResponse> => {
    return apiRequest(
      `/admin/organisations/${orgId}/contracts${buildQuery({
        status: params?.status,
        limit: params?.limit,
        nextToken: params?.nextToken,
      })}`,
    );
  },

  listGlobalAdminUsers: (params?: {
    limit?: number;
    nextToken?: string;
  }): Promise<{ items: GlobalAdminUser[]; nextToken?: string }> => {
    return apiRequest(
      `/admin/users${buildQuery({
        limit: params?.limit,
        nextToken: params?.nextToken,
      })}`,
    );
  },

  createGlobalAdminUser: (
    input: CreateGlobalAdminUserInput,
  ): Promise<GlobalAdminUser> => {
    return apiRequest("/admin/users", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  deleteGlobalAdminUser: (id: string): Promise<void> => {
    return apiRequest(`/admin/users/${id}`, {
      method: "DELETE",
    });
  },

  resendGlobalAdminInvitation: (
    userId: string,
  ): Promise<{ resent: boolean }> => {
    return apiRequest(`/admin/users/${userId}/resend-invitation`, {
      method: "POST",
    });
  },

  createOrganisationUser: (
    orgId: string,
    input: CreateOrganisationUserInput,
  ): Promise<OrganisationUser> => {
    return apiRequest(`/admin/organisations/${orgId}/users`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  updateOrganisationUser: (
    orgId: string,
    userId: string,
    input: UpdateOrganisationUserInput,
  ): Promise<OrganisationUser> => {
    return apiRequest(`/admin/organisations/${orgId}/users/${userId}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  deleteOrganisationUser: (orgId: string, userId: string): Promise<void> => {
    return apiRequest(`/admin/organisations/${orgId}/users/${userId}`, {
      method: "DELETE",
    });
  },

  disableOrganisationUser: (
    orgId: string,
    userId: string,
  ): Promise<OrganisationUser> => {
    return apiRequest(`/admin/organisations/${orgId}/users/${userId}/disable`, {
      method: "POST",
    });
  },

  enableOrganisationUser: (
    orgId: string,
    userId: string,
  ): Promise<OrganisationUser> => {
    return apiRequest(`/admin/organisations/${orgId}/users/${userId}/enable`, {
      method: "POST",
    });
  },

  getOrganisationContract: (
    orgId: string,
    contractId: string,
  ): Promise<Contract> => {
    return apiRequest(`/admin/organisations/${orgId}/contracts/${contractId}`);
  },

  getStats: (): Promise<OrganisationStats> => {
    return apiRequest("/admin/stats");
  },

  createOrgBackup: (
    orgId: string,
  ): Promise<{ backupId: string; status: string }> =>
    apiRequest(`/admin/organisations/${orgId}/backup`, { method: "POST" }),

  getOrgBackupStatus: (
    orgId: string,
    backupId: string,
  ): Promise<BackupStatus> =>
    apiRequest(`/admin/organisations/${orgId}/backup/${backupId}`),
};

export interface BackupStatus {
  backupId: string;
  status: "pending" | "processing" | "completed" | "failed";
  downloadUrl?: string;
  filename?: string;
  error?: string;
  createdAt: string;
}

export const orgTemplatesApi = {
  list: (params?: {
    limit?: number;
    nextToken?: string;
  }): Promise<OrgTemplateListResponse> => {
    return apiRequest(
      `/templates/org${buildQuery({
        limit: params?.limit,
        nextToken: params?.nextToken,
      })}`,
    );
  },

  get: (id: string): Promise<OrgTemplate> => {
    return apiRequest(`/templates/org/${id}`);
  },

  create: (input: CreateOrgTemplateInput): Promise<OrgTemplate> => {
    return apiRequest("/templates/org", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  update: (id: string, input: UpdateOrgTemplateInput): Promise<OrgTemplate> => {
    return apiRequest(`/templates/org/${id}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  deactivate: (id: string): Promise<void> => {
    return apiRequest(`/templates/org/${id}`, {
      method: "DELETE",
    });
  },

  getUploadUrl: (id: string): Promise<UploadUrlResponse> => {
    return apiRequest(`/templates/org/${id}/upload-url`, {
      method: "POST",
    });
  },

  /**
   * Two-phase upload: presigned PUT for the staged docx. `stagedId` is
   * server-minted (ULID) and echoed back on `stageVersion` below.
   */
  stagedUploadUrl: (id: string): Promise<StagedUploadUrlResponse> => {
    return apiRequest(`/templates/org/${id}/staged-versions/upload-url`, {
      method: "POST",
    });
  },

  /**
   * Stage an uploaded docx for review. `changeNotes` is omitted from the
   * body entirely when empty — no null/empty-string noise on the wire.
   */
  stageVersion: (
    id: string,
    input: StageVersionInput,
  ): Promise<StageVersionResponse> => {
    const body: { stagedId: string; changeNotes?: string } = {
      stagedId: input.stagedId,
    };
    if (input.changeNotes) {
      body.changeNotes = input.changeNotes;
    }
    return apiRequest(`/templates/org/${id}/staged-versions`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  promoteStagedVersion: (
    id: string,
    stagedId: string,
  ): Promise<TemplateVersion & { extractionWarning?: string }> => {
    return apiRequest(`/templates/org/${id}/staged-versions/${stagedId}/promote`, {
      method: "POST",
    });
  },

  discardStagedVersion: (id: string, stagedId: string): Promise<void> => {
    return apiRequest(`/templates/org/${id}/staged-versions/${stagedId}`, {
      method: "DELETE",
    });
  },

  getStagedSampleUrl: (id: string, stagedId: string): Promise<{ url: string }> => {
    return apiRequest(`/templates/org/${id}/staged-versions/${stagedId}/sample-url`);
  },

  listVersions: (
    id: string,
    params?: { limit?: number; nextToken?: string },
  ): Promise<TemplateVersionListResponse> => {
    return apiRequest(
      `/templates/org/${id}/versions${buildQuery({
        limit: params?.limit,
        nextToken: params?.nextToken,
      })}`,
    );
  },

  getVersionDownloadUrl: (
    id: string,
    version: number,
  ): Promise<{ url: string }> => {
    return apiRequest(`/templates/org/${id}/versions/${version}/download-url`);
  },

  rollbackVersion: (
    id: string,
    version: number,
  ): Promise<OrgTemplate & { extractionWarning?: string }> => {
    return apiRequest(`/templates/org/${id}/versions/${version}/rollback`, {
      method: "POST",
    });
  },

  updateFieldMappings: (
    id: string,
    input: UpdateFieldMappingsInput,
  ): Promise<OrgTemplate> => {
    return apiRequest(`/templates/org/${id}/field-mappings`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  updateSchema: (
    id: string,
    schema: OrgTemplateSchema,
  ): Promise<OrgTemplate> => {
    return apiRequest(`/templates/org/${id}/schema`, {
      method: "PUT",
      body: JSON.stringify(schema),
    });
  },

  /**
   * Get presigned URL for downloading reference PDF.
   * Returns null if no reference PDF is configured.
   */
  getReferencePdfUrl: (id: string): Promise<ReferencePdfUrlResponse> => {
    return apiRequest(`/templates/org/${id}/reference-pdf-url`);
  },

  /**
   * Get presigned URL for uploading reference PDF (admin only).
   */
  getReferencePdfUploadUrl: (
    id: string,
  ): Promise<ReferencePdfUploadUrlResponse> => {
    return apiRequest(`/templates/org/${id}/reference-pdf-upload-url`, {
      method: "POST",
    });
  },

  /**
   * Confirm reference PDF upload (admin only).
   */
  confirmReferencePdf: (id: string, pdfKey: string): Promise<OrgTemplate> => {
    return apiRequest(`/templates/org/${id}/reference-pdf`, {
      method: "PUT",
      body: JSON.stringify({ pdfKey }),
    });
  },

  /**
   * Remove reference PDF (admin only).
   */
  deleteReferencePdf: (id: string): Promise<void> => {
    return apiRequest(`/templates/org/${id}/reference-pdf`, {
      method: "DELETE",
    });
  },

  // ==================== Per-Module Reference PDF Methods ====================

  /**
   * Update reference PDF mode ('single' or 'per-module').
   */
  updateReferencePdfMode: (
    id: string,
    mode: ReferencePdfMode,
    cleanupOldFiles?: boolean,
  ): Promise<OrgTemplate> => {
    return apiRequest(`/templates/org/${id}/reference-pdf-mode`, {
      method: "PUT",
      body: JSON.stringify({ mode, cleanupOldFiles }),
    });
  },

  /**
   * Get presigned upload URL for a module's reference PDF.
   */
  getModulePdfUploadUrl: (
    id: string,
    moduleId: string,
  ): Promise<ReferencePdfUploadUrlResponse> => {
    return apiRequest(
      `/templates/org/${id}/reference-pdf/module/${encodeURIComponent(moduleId)}/upload-url`,
      { method: "POST" },
    );
  },

  /**
   * Confirm module PDF upload.
   */
  confirmModulePdf: (
    id: string,
    moduleId: string,
    key: string,
  ): Promise<OrgTemplate> => {
    return apiRequest(
      `/templates/org/${id}/reference-pdf/module/${encodeURIComponent(moduleId)}`,
      {
        method: "PUT",
        body: JSON.stringify({ key }),
      },
    );
  },

  /**
   * Delete a module's reference PDF.
   */
  deleteModulePdf: (id: string, moduleId: string): Promise<void> => {
    return apiRequest(
      `/templates/org/${id}/reference-pdf/module/${encodeURIComponent(moduleId)}`,
      { method: "DELETE" },
    );
  },

  /**
   * Get presigned download URL for a module's PDF.
   */
  getModulePdfUrl: async (
    id: string,
    moduleId: string,
  ): Promise<ReferencePdfUrlResponse | null> => {
    try {
      return await apiRequest<ReferencePdfUrlResponse>(
        `/templates/org/${id}/reference-pdf/module/${encodeURIComponent(moduleId)}/url`,
      );
    } catch (error) {
      // Return null for 404 (no PDF configured for this module)
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },

  /**
   * Get presigned upload URL for contract details reference PDF.
   */
  getContractDetailsPdfUploadUrl: (
    id: string,
  ): Promise<ReferencePdfUploadUrlResponse> => {
    return apiRequest(
      `/templates/org/${id}/reference-pdf/contract-details/upload-url`,
      {
        method: "POST",
      },
    );
  },

  /**
   * Confirm contract details PDF upload.
   */
  confirmContractDetailsPdf: (
    id: string,
    key: string,
  ): Promise<OrgTemplate> => {
    return apiRequest(`/templates/org/${id}/reference-pdf/contract-details`, {
      method: "PUT",
      body: JSON.stringify({ key }),
    });
  },

  /**
   * Delete contract details reference PDF.
   */
  deleteContractDetailsPdf: (id: string): Promise<void> => {
    return apiRequest(`/templates/org/${id}/reference-pdf/contract-details`, {
      method: "DELETE",
    });
  },

  /**
   * Get presigned download URL for contract details PDF.
   */
  getContractDetailsPdfUrl: async (
    id: string,
  ): Promise<ReferencePdfUrlResponse | null> => {
    try {
      return await apiRequest<ReferencePdfUrlResponse>(
        `/templates/org/${id}/reference-pdf/contract-details/url`,
      );
    } catch (error) {
      // Return null for 404 (no contract-details PDF configured)
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
};

export const orgUsersApi = {
  list: (orgId: string): Promise<OrganisationUserListResponse> => {
    return apiRequest(`/organisations/${orgId}/users`);
  },

  create: (
    orgId: string,
    input: CreateOrganisationUserInput,
  ): Promise<OrganisationUser> => {
    return apiRequest(`/organisations/${orgId}/users`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  update: (
    orgId: string,
    userId: string,
    input: UpdateOrganisationUserInput,
  ): Promise<OrganisationUser> => {
    return apiRequest(`/organisations/${orgId}/users/${userId}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  disable: (orgId: string, userId: string): Promise<OrganisationUser> => {
    return apiRequest(`/organisations/${orgId}/users/${userId}/disable`, {
      method: "POST",
    });
  },

  enable: (orgId: string, userId: string): Promise<OrganisationUser> => {
    return apiRequest(`/organisations/${orgId}/users/${userId}/enable`, {
      method: "POST",
    });
  },

  resendInvitation: (
    orgId: string,
    userId: string,
  ): Promise<{ resent: boolean }> => {
    return apiRequest(
      `/organisations/${orgId}/users/${userId}/resend-invitation`,
      { method: "POST" },
    );
  },
};

export const profileApi = {
  get: (): Promise<ProfileResponse> => {
    return apiRequest("/profile");
  },

  update: (input: UpdateProfileInput): Promise<ProfileResponse> => {
    return apiRequest("/profile", {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  getNotifications: (): Promise<NotificationPreferences> => {
    return apiRequest<NotificationPreferences>("/profile/notifications");
  },

  updateNotifications: (
    body: UpdateNotificationPreferences,
  ): Promise<NotificationPreferences> => {
    return apiRequest<NotificationPreferences>("/profile/notifications", {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  getNavRecents: (): Promise<NavRecentsResponse> => {
    return apiRequest<NavRecentsResponse>("/profile/nav-recents");
  },

  // Full replace of the user's palette recents list. The backend validates
  // entries with strict per-kind schemas — callers must send ONLY the
  // persisted fields (see types/navRecents.ts).
  updateNavRecents: (entries: RecentEntry[]): Promise<NavRecentsResponse> => {
    return apiRequest<NavRecentsResponse>("/profile/nav-recents", {
      method: "PUT",
      body: JSON.stringify({ entries }),
    });
  },
};

/**
 * Contract-level ACL grant CRUD. The grant payload returns
 * the full ACL row including the source-set so the UI can surface
 * "User retains access via owner/manager link" on revoke-with-auto.
 */
export interface ContractAclUserEntry {
  userId: string;
  name: string;
  email: string;
  sources: Array<"manual" | "auto-owner" | "auto-manager">;
  firstGrantedAt: string;
}

export interface ContractAccessResponse {
  contractId: string;
  users: ContractAclUserEntry[];
}

export interface GrantContractAclResponse {
  contractId: string;
  userId: string;
  sources: Array<"manual" | "auto-owner" | "auto-manager">;
  firstGrantedAt: string;
}

export interface RevokeContractAclResponse {
  contractId: string;
  userId: string;
  deleted: boolean;
  /** True when auto-owner / auto-manager sources kept the row alive after
   *  manual revocation; the user retains access via the linked role. */
  accessRetained: boolean;
}

export interface UserContractGrantEntry {
  contractId: string;
  contractNumber: string;
  title: string;
}

export interface UserContractGrantsResponse {
  items: UserContractGrantEntry[];
}

export const contractAclApi = {
  get: (contractId: string): Promise<ContractAccessResponse> => {
    return apiRequest(
      `/manage/contracts/${encodeURIComponent(contractId)}/access`,
    );
  },

  grant: (
    contractId: string,
    userId: string,
  ): Promise<GrantContractAclResponse> => {
    return apiRequest(
      `/manage/contracts/${encodeURIComponent(contractId)}/access/users`,
      {
        method: "POST",
        body: JSON.stringify({ userId }),
      },
    );
  },

  revoke: (
    contractId: string,
    userId: string,
  ): Promise<RevokeContractAclResponse> => {
    return apiRequest(
      `/manage/contracts/${encodeURIComponent(contractId)}/access/users/${encodeURIComponent(userId)}`,
      { method: "DELETE" },
    );
  },

  // List contracts a specific user holds ACL grants on. Used by the
  // Edit User modal to seed the contract-grants picker with the current
  // grants and compute a diff on save.
  listGrantsForUser: (
    orgId: string,
    userId: string,
  ): Promise<UserContractGrantsResponse> => {
    return apiRequest(
      `/organisations/${encodeURIComponent(orgId)}/users/${encodeURIComponent(userId)}/contract-grants`,
    );
  },
};

// Bulk user-display lookup for attribution on tracking meta lines.
// Returns `{ users: [...] }` — unresolved / disabled / cross-tenant
// IDs are omitted, NOT returned as error entries. Empty input
// short-circuits without hitting the network.
export const usersApi = {
  batchLookup: (ids: string[]): Promise<UserDisplayBatchResponse> => {
    if (ids.length === 0) return Promise.resolve({ users: [] });
    const qs = new URLSearchParams({ ids: ids.join(",") }).toString();
    return apiRequest<UserDisplayBatchResponse>(`/users?${qs}`);
  },
};

export const lookupsApi = {
  list: (
    orgId: string,
    category: LookupCategory,
  ): Promise<LookupItemListResponse> => {
    return apiRequest(`/organisations/${orgId}/lookups/${category}`);
  },

  create: (
    orgId: string,
    input: CreateLookupItemInput,
  ): Promise<LookupItem> => {
    return apiRequest(`/organisations/${orgId}/lookups`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  update: (
    orgId: string,
    itemId: string,
    input: UpdateLookupItemInput,
  ): Promise<LookupItem> => {
    return apiRequest(`/organisations/${orgId}/lookups/${itemId}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },
};

export const suppliersApi = {
  list: (params?: {
    search?: string;
    status?: string;
  }): Promise<SupplierListResponse> => {
    return apiRequest(
      `/manage/suppliers${buildQuery({
        search: params?.search,
        status: params?.status,
      })}`,
    );
  },

  get: (id: string): Promise<SupplierDetailResponse> => {
    return apiRequest(`/manage/suppliers/${id}`);
  },

  create: (input: CreateSupplierInput): Promise<Supplier> => {
    return apiRequest("/manage/suppliers", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  update: (id: string, input: UpdateSupplierInput): Promise<Supplier> => {
    return apiRequest(`/manage/suppliers/${id}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },
};

export const managedContractsApi = {
  list: (params?: {
    search?: string;
    type?: string;
    status?: string;
    supplierId?: string;
    contractTypeId?: string;
    businessUnitId?: string;
    expiryWithinDays?: number;
    limit?: number;
    nextToken?: string;
  }): Promise<ManagedContractListResponse> => {
    return apiRequest(
      `/manage/contracts${buildQuery({
        search: params?.search,
        type: params?.type,
        status: params?.status,
        supplierId: params?.supplierId,
        contractTypeId: params?.contractTypeId,
        businessUnitId: params?.businessUnitId,
        expiryWithinDays: params?.expiryWithinDays,
        limit: params?.limit,
        nextToken: params?.nextToken,
      })}`,
    );
  },

  // Contract Register full-drain path: `view=register` opts in to the
  // {items, summary, complete, lastComputedAt, summaryScope} envelope.
  // Without it the backend returns the old {items,nextToken} shape, so
  // this param is always required. No `limit`/`nextToken` — the backend
  // full-drains the viewer-scoped set when view=register is present.
  listRegister: (params?: {
    search?: string;
    type?: string;
    status?: string;
    supplierId?: string;
    contractTypeId?: string;
    businessUnitId?: string;
    grade?: string;
  }): Promise<ContractRegisterResponse> => {
    return apiRequest(
      `/manage/contracts${buildQuery({
        view: 'register',
        search: params?.search,
        type: params?.type,
        status: params?.status,
        supplierId: params?.supplierId,
        contractTypeId: params?.contractTypeId,
        businessUnitId: params?.businessUnitId,
        grade: params?.grade,
      })}`,
    );
  },

  get: (id: string): Promise<ManagedContract> => {
    return apiRequest(`/manage/contracts/${id}`);
  },

  create: (input: CreateManagedContractInput): Promise<ManagedContract> => {
    return apiRequest("/manage/contracts", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  update: (
    id: string,
    input: UpdateManagedContractInput,
  ): Promise<ManagedContract> => {
    return apiRequest(`/manage/contracts/${id}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  getPerformanceHistory: (
    contractId: string,
    params?: { since?: string; until?: string },
  ): Promise<PerformanceHistoryResponse> => {
    return apiRequest<PerformanceHistoryResponse>(
      `/manage/contracts/${encodeURIComponent(contractId)}/performance-history${buildQuery(
        { since: params?.since, until: params?.until },
      )}`,
    );
  },
};

// ─── Tracking API ──────────────────────────────────────────────────────
// Every method declares an explicit Promise<T> return and passes the generic
// to apiRequest<T>() — avoids the Promise<{}> gotcha from project memory.

function buildTrackingQuery(params?: TrackingListParams): string {
  const searchParams = new URLSearchParams();
  if (params?.limit != null) searchParams.set("limit", String(params.limit));
  if (params?.nextToken) searchParams.set("nextToken", params.nextToken);
  if (params?.includeArchived) searchParams.set("includeArchived", "true");
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export const trackingApi = {
  list: (
    contractId: string,
    type: TrackingType,
    params?: TrackingListParams,
  ): Promise<TrackingItemListResponse> => {
    // ID format is validated at the routing boundary. Hook trusts inputs.
    return apiRequest<TrackingItemListResponse>(
      `/manage/contracts/${encodeURIComponent(contractId)}/tracking/${encodeURIComponent(type)}${buildTrackingQuery(params)}`,
    );
  },

  create: (
    contractId: string,
    type: TrackingType,
    input: CreateTrackingInput,
  ): Promise<TrackingItem> => {
    return apiRequest<TrackingItem>(
      `/manage/contracts/${encodeURIComponent(contractId)}/tracking/${encodeURIComponent(type)}`,
      {
        method: "POST",
        body: JSON.stringify(input),
      },
    );
  },

  get: (
    contractId: string,
    type: TrackingType,
    itemId: string,
  ): Promise<TrackingItem> => {
    return apiRequest<TrackingItem>(
      `/manage/contracts/${encodeURIComponent(contractId)}/tracking/${encodeURIComponent(type)}/${encodeURIComponent(itemId)}`,
    );
  },

  update: (
    contractId: string,
    type: TrackingType,
    itemId: string,
    input: UpdateTrackingInput,
  ): Promise<TrackingItem> => {
    return apiRequest<TrackingItem>(
      `/manage/contracts/${encodeURIComponent(contractId)}/tracking/${encodeURIComponent(type)}/${encodeURIComponent(itemId)}`,
      {
        method: "PUT",
        body: JSON.stringify(input),
      },
    );
  },

  archive: (
    contractId: string,
    type: TrackingType,
    itemId: string,
    reason: string,
  ): Promise<TrackingItem> => {
    const body: ArchiveTrackingInput = { reason };
    return apiRequest<TrackingItem>(
      `/manage/contracts/${encodeURIComponent(contractId)}/tracking/${encodeURIComponent(type)}/${encodeURIComponent(itemId)}/archive`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  },

  // POST .../tracking/variation/{itemId}/approve — empty body. The
  // `type` parameter is restricted to the literal `"variation"` so a
  // typo or accidental call with another type fails at compile time
  // rather than reaching the backend (which would 400).
  approve: (
    contractId: string,
    type: "variation",
    itemId: string,
  ): Promise<Variation> => {
    return apiRequest<Variation>(
      `/manage/contracts/${encodeURIComponent(contractId)}/tracking/${encodeURIComponent(type)}/${encodeURIComponent(itemId)}/approve`,
      {
        method: "POST",
        body: JSON.stringify({}),
      },
    );
  },

  // POST .../tracking/variation/{itemId}/reject — optional reason in
  // body. Same compile-time guard against non-variation types.
  reject: (
    contractId: string,
    type: "variation",
    itemId: string,
    body: RejectVariationInput,
  ): Promise<Variation> => {
    return apiRequest<Variation>(
      `/manage/contracts/${encodeURIComponent(contractId)}/tracking/${encodeURIComponent(type)}/${encodeURIComponent(itemId)}/reject`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  },

  // POST .../tracking/sow/{itemId}/correct-original-value — editor-gated
  // audit-logged repair for an originalValue typo. Required reason ≥10
  // chars. Type literal `"sow"` so a non-SOW caller fails at compile time.
  correctSowOriginalValue: (
    contractId: string,
    type: "sow",
    itemId: string,
    body: CorrectOriginalValueInput,
  ): Promise<Sow> => {
    return apiRequest<Sow>(
      `/manage/contracts/${encodeURIComponent(contractId)}/tracking/${encodeURIComponent(type)}/${encodeURIComponent(itemId)}/correct-original-value`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  },

  // POST .../tracking/sow/{itemId}/correct-status — editor-gated audit
  // endpoint that bypasses the SOW state-machine for legitimate
  // corrections (e.g. clerical mis-cancellation). Required reason ≥10.
  correctSowStatus: (
    contractId: string,
    type: "sow",
    itemId: string,
    body: CorrectStatusInput,
  ): Promise<Sow> => {
    return apiRequest<Sow>(
      `/manage/contracts/${encodeURIComponent(contractId)}/tracking/${encodeURIComponent(type)}/${encodeURIComponent(itemId)}/correct-status`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  },

  // POST .../tracking/variation/{itemId}/correct-variation — editor-gated
  // multi-field correction with audit row. Bypasses the
  // valueImpact status-gate so an Approved or Rejected variation can be
  // corrected for genuine data-entry mistakes.
  correctVariation: (
    contractId: string,
    type: "variation",
    itemId: string,
    body: CorrectVariationInput,
  ): Promise<Variation> => {
    return apiRequest<Variation>(
      `/manage/contracts/${encodeURIComponent(contractId)}/tracking/${encodeURIComponent(type)}/${encodeURIComponent(itemId)}/correct-variation`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  },

  // ─── Issue command endpoints ─────────────────────────────────────────
  // Lifecycle + thread mutations live on dedicated POST routes (the
  // generic PUT only edits metadata). All editor-gated server-side. Each
  // returns the updated Issue so React Query can refresh the item cache.

  // POST .../tracking/issue/{itemId}/transition — any status change,
  // incl. →resolved (server stamps resolvedAt) and resolved→open re-open.
  // Server auto-logs a status_change thread entry. No separate /resolve
  // endpoint — resolving is a transition with `to: 'resolved'`.
  transitionIssue: (
    contractId: string,
    itemId: string,
    body: TransitionIssueInput,
  ): Promise<Issue> => {
    return apiRequest<Issue>(
      `/manage/contracts/${encodeURIComponent(contractId)}/tracking/issue/${encodeURIComponent(itemId)}/transition`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  },

  // POST .../tracking/issue/{itemId}/close — close with a required outcome
  // reason + optional note.
  closeIssue: (
    contractId: string,
    itemId: string,
    body: CloseIssueInput,
  ): Promise<Issue> => {
    return apiRequest<Issue>(
      `/manage/contracts/${encodeURIComponent(contractId)}/tracking/issue/${encodeURIComponent(itemId)}/close`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  },

  // POST .../tracking/issue/{itemId}/thread — append a free-text comment.
  appendIssueComment: (
    contractId: string,
    itemId: string,
    body: AppendThreadCommentInput,
  ): Promise<Issue> => {
    return apiRequest<Issue>(
      `/manage/contracts/${encodeURIComponent(contractId)}/tracking/issue/${encodeURIComponent(itemId)}/thread`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  },

  // POST .../tracking/issue/{itemId}/thread/action — raise a committed
  // action (party + optional internal owner + due date).
  appendIssueAction: (
    contractId: string,
    itemId: string,
    body: AppendThreadActionInput,
  ): Promise<Issue> => {
    return apiRequest<Issue>(
      `/manage/contracts/${encodeURIComponent(contractId)}/tracking/issue/${encodeURIComponent(itemId)}/thread/action`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  },

  // POST .../tracking/issue/{itemId}/thread/{entryId}/action-done — mark the
  // action raised by {entryId} done. Server stamps the completion date and
  // writes the paired action_done thread entry.
  markIssueActionDone: (
    contractId: string,
    itemId: string,
    entryId: string,
    body: MarkActionDoneInput,
  ): Promise<Issue> => {
    return apiRequest<Issue>(
      `/manage/contracts/${encodeURIComponent(contractId)}/tracking/issue/${encodeURIComponent(itemId)}/thread/${encodeURIComponent(entryId)}/action-done`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  },

  generateOccurrences: (
    contractId: string,
    type: TrackingType,
    itemId: string,
  ): Promise<GenerateOccurrencesResponse> => {
    return apiRequest<GenerateOccurrencesResponse>(
      `/manage/contracts/${encodeURIComponent(contractId)}/tracking/${encodeURIComponent(type)}/${encodeURIComponent(itemId)}/generate-occurrences`,
      { method: "POST" },
    );
  },

  /**
   * Dry-run preview of the generate-occurrences endpoint. Backend returns
   * `{count, occurrenceDates, capped}` without writing. Required by the
   * Generate Occurrences confirmation dialog to show an exact count +
   * date strip before the user commits to the write.
   */
  previewOccurrences: (
    contractId: string,
    type: TrackingType,
    itemId: string,
  ): Promise<OccurrencePreviewResponse> => {
    return apiRequest<OccurrencePreviewResponse>(
      `/manage/contracts/${encodeURIComponent(contractId)}/tracking/${encodeURIComponent(type)}/${encodeURIComponent(itemId)}/occurrence-preview`,
    );
  },

  counts: (contractId: string): Promise<TrackingCountsResponse> => {
    return apiRequest<TrackingCountsResponse>(
      `/manage/contracts/${encodeURIComponent(contractId)}/tracking/counts`,
    );
  },

  // POST /manage/contracts/{contractId}/tracking/kpi-period-batch
  // Bulk-writes per-KPI measurement + report pairs. Sequential on the
  // server (not TransactWrite) — partial success is the contract.
  kpiPeriodBatch: (
    contractId: string,
    body: KpiBatchRequest,
  ): Promise<KpiBatchResponse> => {
    return apiRequest<KpiBatchResponse>(
      `/manage/contracts/${encodeURIComponent(contractId)}/tracking/kpi-period-batch`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  },

  orgWideList: (
    type: TrackingType,
    params?: TrackingListParams,
  ): Promise<TrackingItemListResponse> => {
    return apiRequest<TrackingItemListResponse>(
      `/manage/tracking/${encodeURIComponent(type)}${buildTrackingQuery(params)}`,
    );
  },

  summary: (): Promise<TrackingCountsResponse> => {
    return apiRequest<TrackingCountsResponse>("/manage/tracking/summary");
  },

  // GET .../tracking/milestone/{milestoneId}/linked-deliverables
  // Returns deliverables whose milestoneId FK points at the given milestone —
  // the single canonical link between a milestone and its deliverables.
  linkedDeliverables: (
    contractId: string,
    milestoneId: string,
  ): Promise<TrackingItemListResponse> => {
    return apiRequest<TrackingItemListResponse>(
      `/manage/contracts/${encodeURIComponent(contractId)}/tracking/milestone/${encodeURIComponent(milestoneId)}/linked-deliverables`,
    );
  },

  // PUT .../tracking/milestone/{milestoneId}/linked-deliverables
  // Idempotent bulk-set of the deliverables whose milestoneId FK points at
  // the given milestone. The picker UI surfaces cross-milestone deliverables
  // as disabled (so they never appear in `deliverableIds`); a stray cross-
  // milestone id will be rejected by the backend with 409.
  setLinkedDeliverables: (
    contractId: string,
    milestoneId: string,
    deliverableIds: string[],
  ): Promise<TrackingItemListResponse> => {
    return apiRequest<TrackingItemListResponse>(
      `/manage/contracts/${encodeURIComponent(contractId)}/tracking/milestone/${encodeURIComponent(milestoneId)}/linked-deliverables`,
      {
        method: "PUT",
        body: JSON.stringify({ deliverableIds }),
      },
    );
  },
};

export const activityApi = {
  list: (
    contractId: string,
    params?: {
      eventTypes?: string;
      since?: string;
      until?: string;
      limit?: number;
      cursor?: string;
    },
  ) => {
    const qs = new URLSearchParams();
    if (params?.eventTypes) qs.set("eventTypes", params.eventTypes);
    if (params?.since) qs.set("since", params.since);
    if (params?.until) qs.set("until", params.until);
    if (params?.limit !== undefined) qs.set("limit", String(params.limit));
    if (params?.cursor) qs.set("cursor", params.cursor);
    const query = qs.toString();
    return apiRequest<import("../types").ActivityPage>(
      `/manage/contracts/${encodeURIComponent(contractId)}/activity${query ? `?${query}` : ""}`,
    );
  },
};

export const exportApi = {
  create: (contractId: string, input: CreateExportInput) =>
    apiRequest<CreateExportResponse>(
      `/manage/contracts/${encodeURIComponent(contractId)}/exports`,
      { method: "POST", body: JSON.stringify(input) },
    ),

  // A PDF of one Reports-tab view. Same route as a contract export; the
  // server captures the report model at request time, so a retry carrying the
  // same requestToken returns the export it already started.
  requestReport: (contractId: string, input: CreateReportExportInput) =>
    apiRequest<CreateExportResponse>(
      `/manage/contracts/${encodeURIComponent(contractId)}/exports`,
      { method: "POST", body: JSON.stringify(input) },
    ),

  get: (contractId: string, exportId: string) =>
    apiRequest<ContractExport>(
      `/manage/contracts/${encodeURIComponent(contractId)}/exports/${encodeURIComponent(exportId)}`,
    ),

  list: (contractId: string) =>
    apiRequest<{ items: ContractExport[] }>(
      `/manage/contracts/${encodeURIComponent(contractId)}/exports`,
    ),

  // Download an export artefact via an authenticated fetch so the S3 host
  // never appears in the address bar or in JS. The backend streams the file
  // bytes directly (Content-Type: application/pdf or …xlsx). A JSON
  // { url } fallback is handled transparently for large-export edge cases:
  // when detected, the presigned URL is opened via window.open (single-use,
  // short-lived). For the binary path the blob is saved via a synthetic
  // <a download> click without exposing any S3 URL.
  downloadExport: async (
    contractId: string,
    exportId: string,
    format: "pdf" | "xlsx",
  ): Promise<void> => {
    // Reuse the same auth + retry logic as apiRequest by calling performRequest
    // directly — apiRequest assumes a JSON response body, which we can't use here.
    let response: Response;
    const endpoint = `/manage/contracts/${encodeURIComponent(contractId)}/exports/${encodeURIComponent(exportId)}/download?format=${format}`;
    try {
      response = await performRequest(endpoint, {}, false);
      if (response.status === 401) {
        response = await performRequest(endpoint, {}, true);
      }
    } catch (err) {
      throw new ApiError(err instanceof Error ? err.message : "Network error", 0);
    }
    if (response.status === 401) {
      await authExpiryHandler();
      throw new ApiError("Session expired", 401, "SESSION_EXPIRED");
    }
    if (!response.ok) {
      throw new ApiError(`HTTP ${response.status}`, response.status);
    }

    const contentType = response.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) {
      // Legacy / large-export fallback: backend returned { url } instead of
      // streaming bytes. Open the presigned URL directly.
      const data = (await response.json()) as { url: string };
      window.open(data.url, "_blank", "noopener,noreferrer");
      return;
    }

    // Binary stream path: build a blob URL, trigger a native save, then
    // immediately revoke so the object URL doesn't leak memory.
    const blob = await response.blob();
    const disposition = response.headers.get("content-disposition") ?? "";
    const filenameMatch = /filename="?([^";]+)"?/.exec(disposition);
    const ext = format === "xlsx" ? "xlsx" : "pdf";
    const filename = filenameMatch?.[1] ?? `export-${exportId}.${ext}`;

    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(objectUrl);
  },

  // Per-issue PDF export request. The body is empty — the URL carries the
  // identity. Returns { exportId } that the drawer polls via `get` and
  // resolves via `downloadExport` once status reaches 'ready'.
  requestForIssue: (contractId: string, itemId: string) =>
    apiRequest<CreateExportResponse>(
      `/manage/contracts/${encodeURIComponent(contractId)}/tracking/issue/${encodeURIComponent(itemId)}/export`,
      { method: "POST", body: JSON.stringify({}) },
    ),
};

export const organisationsApi = {
  get: (id: string): Promise<Organisation> => {
    return apiRequest<Organisation>(`/organisations/${id}`);
  },

  update: (id: string, data: UpdateOrganisationInput): Promise<Organisation> => {
    return apiRequest<Organisation>(`/organisations/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  listUsers: (id: string): Promise<OrganisationUserListResponse> => {
    return apiRequest<OrganisationUserListResponse>(`/organisations/${id}/users`);
  },
};

// Cross-contract schedule aggregation. The response is typed
// explicitly via apiRequest<ScheduleResponse>() so consumers don't hit
// the untyped-apiRequest Promise<{}> gotcha. `contracts`/`kinds` are
// CSV; omitting one means "all". `from`/`to` are YYYY-MM-DD and
// required by the endpoint. Status-bucket narrowing is a client-side
// concern (see scheduleBuckets.ts) — the backend's severity-status
// filter is no longer used by this page.
export const scheduleApi = {
  list: (params: {
    from: string;
    to: string;
    contracts?: string;
    kinds?: string;
    search?: string;
  }): Promise<import("../types/schedule").ScheduleResponse> =>
    apiRequest<import("../types/schedule").ScheduleResponse>(
      `/manage/schedule${buildQuery({
        from: params.from,
        to: params.to,
        contracts: params.contracts,
        kinds: params.kinds,
        search: params.search,
      })}`,
    ),

  // Phase-2 detail endpoint. Typed explicitly via
  // apiRequest<ActivityDetail> so consumers don't hit the
  // untyped-apiRequest Promise<{}> gotcha.
  activityDetail: (
    activityId: string,
  ): Promise<import("../types/schedule").ActivityDetail> =>
    apiRequest<import("../types/schedule").ActivityDetail>(
      `/manage/schedule/activity/${encodeURIComponent(activityId)}`,
    ),
};

// ─── Finance API ───────────────────────────────────────────────────────
// Purchase-order + summary + invoice + finance-setup surfaces for the
// Finance Tab. Every method declares an explicit Promise<T> return and
// passes the generic to apiRequest<T>() — avoids the Promise<{}> gotcha from
// project memory. Response shapes are matched field-for-field against
// backend/src/handlers/finance.ts — see the header comment in
// ../types/finance.ts for the exact wire shapes, including the fact that
// PO create/update return the bare PurchaseOrder with no derived
// lineBalances/position (only GET routes attach those), and invoice
// create/update/query return the bare Invoice with no `derived` attached
// (only GET invoice routes attach that).
export const financeApi = {
  getSummary: (contractId: string): Promise<FinanceSummary> => {
    return apiRequest<FinanceSummary>(
      `/manage/contracts/${encodeURIComponent(contractId)}/finance/summary`,
    );
  },

  listPurchaseOrders: (
    contractId: string,
  ): Promise<PurchaseOrderListResponse> => {
    return apiRequest<PurchaseOrderListResponse>(
      `/manage/contracts/${encodeURIComponent(contractId)}/finance/purchase-orders`,
    );
  },

  getPurchaseOrder: (
    contractId: string,
    poId: string,
  ): Promise<PurchaseOrderWithDerived> => {
    return apiRequest<PurchaseOrderWithDerived>(
      `/manage/contracts/${encodeURIComponent(contractId)}/finance/purchase-orders/${encodeURIComponent(poId)}`,
    );
  },

  createPurchaseOrder: (
    contractId: string,
    input: CreatePurchaseOrderInput,
  ): Promise<PurchaseOrder> => {
    return apiRequest<PurchaseOrder>(
      `/manage/contracts/${encodeURIComponent(contractId)}/finance/purchase-orders`,
      {
        method: "POST",
        body: JSON.stringify(input),
      },
    );
  },

  updatePurchaseOrder: (
    contractId: string,
    poId: string,
    input: UpdatePurchaseOrderInput,
  ): Promise<PurchaseOrder> => {
    return apiRequest<PurchaseOrder>(
      `/manage/contracts/${encodeURIComponent(contractId)}/finance/purchase-orders/${encodeURIComponent(poId)}`,
      {
        method: "PUT",
        body: JSON.stringify(input),
      },
    );
  },

  deletePurchaseOrder: (contractId: string, poId: string): Promise<void> => {
    return apiRequest(
      `/manage/contracts/${encodeURIComponent(contractId)}/finance/purchase-orders/${encodeURIComponent(poId)}`,
      {
        method: "DELETE",
      },
    );
  },

  listInvoices: (
    contractId: string,
    params?: { status?: InvoiceStatus },
  ): Promise<InvoiceListResponse> => {
    return apiRequest<InvoiceListResponse>(
      `/manage/contracts/${encodeURIComponent(contractId)}/finance/invoices${buildQuery({
        status: params?.status,
      })}`,
    );
  },

  /** Org-wide slim invoice listing (global record search). Manage-area only;
   *  the server post-filters to the caller's accessible contracts. */
  listInvoicesOrgWide: (): Promise<OrgWideInvoiceListResponse> => {
    return apiRequest<OrgWideInvoiceListResponse>("/manage/finance/invoices");
  },

  getInvoice: (
    contractId: string,
    invoiceId: string,
  ): Promise<InvoiceWithDerived> => {
    return apiRequest<InvoiceWithDerived>(
      `/manage/contracts/${encodeURIComponent(contractId)}/finance/invoices/${encodeURIComponent(invoiceId)}`,
    );
  },

  createInvoice: (
    contractId: string,
    input: CreateInvoiceInput,
  ): Promise<Invoice> => {
    return apiRequest<Invoice>(
      `/manage/contracts/${encodeURIComponent(contractId)}/finance/invoices`,
      {
        method: "POST",
        body: JSON.stringify(input),
      },
    );
  },

  updateInvoice: (
    contractId: string,
    invoiceId: string,
    input: UpdateInvoiceInput,
  ): Promise<Invoice> => {
    return apiRequest<Invoice>(
      `/manage/contracts/${encodeURIComponent(contractId)}/finance/invoices/${encodeURIComponent(invoiceId)}`,
      {
        method: "PUT",
        body: JSON.stringify(input),
      },
    );
  },

  deleteInvoice: (contractId: string, invoiceId: string): Promise<void> => {
    return apiRequest(
      `/manage/contracts/${encodeURIComponent(contractId)}/finance/invoices/${encodeURIComponent(invoiceId)}`,
      {
        method: "DELETE",
      },
    );
  },

  approveInvoice: (
    contractId: string,
    invoiceId: string,
    input?: ApproveInvoiceInput,
  ): Promise<ApproveInvoiceResponse> => {
    return apiRequest<ApproveInvoiceResponse>(
      `/manage/contracts/${encodeURIComponent(contractId)}/finance/invoices/${encodeURIComponent(invoiceId)}/approve`,
      { method: "POST", body: JSON.stringify(input ?? {}) },
    );
  },

  queryInvoice: (contractId: string, invoiceId: string): Promise<Invoice> => {
    return apiRequest<Invoice>(
      `/manage/contracts/${encodeURIComponent(contractId)}/finance/invoices/${encodeURIComponent(invoiceId)}/query`,
      { method: "POST" },
    );
  },

  revertInvoice: (
    contractId: string,
    invoiceId: string,
    reason?: string,
  ): Promise<Invoice> => {
    return apiRequest<Invoice>(
      `/manage/contracts/${encodeURIComponent(contractId)}/finance/invoices/${encodeURIComponent(invoiceId)}/revert`,
      { method: "POST", body: JSON.stringify(reason ? { reason } : {}) },
    );
  },

  getSetup: (contractId: string): Promise<SetupResponse> => {
    return apiRequest<SetupResponse>(
      `/manage/contracts/${encodeURIComponent(contractId)}/finance/setup`,
    );
  },

  putSetup: (
    contractId: string,
    input: FinanceSetupPutInput,
  ): Promise<SetupResponse> => {
    return apiRequest<SetupResponse>(
      `/manage/contracts/${encodeURIComponent(contractId)}/finance/setup`,
      {
        method: "PUT",
        body: JSON.stringify(input),
      },
    );
  },
};

// ─── Report API ────────────────────────────────────────────────────────
// The Reports tab's per-contract read models plus the manager-commentary
// write. Every method declares an explicit Promise<T> and passes the generic
// to apiRequest<T>() so the response type carries through to the hook.
// Response shapes are matched field-for-field against
// backend/src/handlers/report.ts:
//   - GET  .../report/summary     → SummaryReportModel
//   - GET  .../report/delivery    → DeliveryReportModel
//   - PUT  .../report/commentary  → the saved ReportCommentary (bare, with
//     the incremented version); a stale version answers 409 with
//     `{ error: { message } }`, which apiRequest raises as an ApiError.
//
// The delivery read names both of its parameters on every request even though
// the server defaults them. A report that echoes the window and the record
// type it answered is only useful if the request that produced it is equally
// explicit in the access log.
export const reportApi = {
  getSummary: (contractId: string): Promise<SummaryReportModel> => {
    return apiRequest<SummaryReportModel>(
      `/manage/contracts/${encodeURIComponent(contractId)}/report/summary`,
    );
  },

  getDelivery: (
    contractId: string,
    range: ReportRange,
    recordType: DeliveryRecordType,
  ): Promise<DeliveryReportModel> => {
    return apiRequest<DeliveryReportModel>(
      `/manage/contracts/${encodeURIComponent(contractId)}/report/delivery${buildQuery(
        { range, recordType },
      )}`,
    );
  },

  getKpi: (
    contractId: string,
    range: ReportRange,
  ): Promise<KpiReportModel> => {
    return apiRequest<KpiReportModel>(
      `/manage/contracts/${encodeURIComponent(contractId)}/report/kpi${buildQuery(
        { range },
      )}`,
    );
  },

  getFinancial: (
    contractId: string,
    range: ReportRange,
  ): Promise<FinancialReportModel> => {
    return apiRequest<FinancialReportModel>(
      `/manage/contracts/${encodeURIComponent(contractId)}/report/financial${buildQuery(
        { range },
      )}`,
    );
  },

  putCommentary: (
    contractId: string,
    input: ReportCommentaryPutInput,
  ): Promise<ReportCommentary> => {
    return apiRequest<ReportCommentary>(
      `/manage/contracts/${encodeURIComponent(contractId)}/report/commentary`,
      {
        method: "PUT",
        body: JSON.stringify(input),
      },
    );
  },

  /** Records or replaces the credit for one contract-month. Answers the saved
   *  record, including the version the next write must carry. */
  putServiceCredit: (
    contractId: string,
    month: string,
    input: ServiceCreditPutInput,
  ): Promise<ServiceCredit> => {
    return apiRequest<ServiceCredit>(
      `/manage/contracts/${encodeURIComponent(
        contractId,
      )}/report/service-credits/${encodeURIComponent(month)}`,
      {
        method: "PUT",
        body: JSON.stringify(input),
      },
    );
  },

  /** Removes a credit. The body is not optional: the server requires the
   *  version the caller read and a reason, both of which reach the audit
   *  entry, and answers 204 on success. */
  deleteServiceCredit: (
    contractId: string,
    month: string,
    input: ServiceCreditDeleteInput,
  ): Promise<void> => {
    return apiRequest<void>(
      `/manage/contracts/${encodeURIComponent(
        contractId,
      )}/report/service-credits/${encodeURIComponent(month)}`,
      {
        method: "DELETE",
        body: JSON.stringify(input),
      },
    );
  },
};
